import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { EmailDesignSelect, useEmailDesignOptions } from './EmailDesignSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SystemEmailDesigns = () => {
  const designs = useEmailDesignOptions();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/email-designer/system-emails`)
      .then((res) => setEmails(res.data.system_emails || []))
      .catch(() => toast.error('Failed to load system emails'))
      .finally(() => setLoading(false));
  }, []);

  const assign = async (key, designId) => {
    setSavingKey(key);
    try {
      await axios.put(`${API}/admin/email-designer/system-emails/${key}`, { design_id: designId });
      setEmails((prev) => prev.map((e) => (e.key === key ? { ...e, design_id: designId } : e)));
      toast.success(designId ? 'Design assigned' : 'Design removed, email is sent as before');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign design');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <Card data-testid="system-email-designs">
      <CardHeader>
        <CardTitle className="text-base">System Emails</CardTitle>
        <CardDescription>Pick the designed template each automatic email uses. Emails without a design are sent exactly as before. Only designs with an Email Content block can be picked.</CardDescription>
      </CardHeader>
      <CardContent className="divide-y">
        {emails.map((e) => (
          <div key={e.key} className="grid grid-cols-1 md:grid-cols-2 gap-3 py-4 items-start" data-testid={`system-email-row-${e.key}`}>
            <div>
              <p className="font-medium text-sm">{e.name}</p>
              <p className="text-xs text-muted-foreground">{e.description}</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <EmailDesignSelect value={e.design_id} onChange={(v) => assign(e.key, v)} designs={designs}
                  testId={`system-email-design-${e.key}`} disabled={savingKey === e.key} showHint={false} />
              </div>
              {savingKey === e.key && <Loader2 className="h-4 w-4 mt-3 animate-spin" />}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
